<script setup lang="ts">
import {
  disabled,
  image,
  download,
  inbox_in,
} from "pn-design-assets/pn-assets/icons";
import type { ImageCategory } from "@/rvapi";
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { local2utc, utc2local } from "@/utils";
import { useImageStore } from "@/stores/images";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import type { PnUploadFileItem } from "@postnord/web-components/types/globals/types";
import type { PnDatePicker } from "@postnord/web-components/components/pn-date-picker";
const router = useRouter();
const route = useRoute();
const imageStore = useImageStore();
const categoryId = route.params.id as string;
const category = ref({} as ImageCategory);
const range = ref<PnDatePicker | null>();
const rangeStart = ref("");
const rangeEnd = ref("");
const rangeError = ref("");
const uploadError = ref("");
const base64 = ref("");
const filename = ref("");
const rangeChange = ref(0);
const iconUrl = computed(() => {
  if (base64.value) {
    return `data:image/png;base64,${base64.value}`;
  }
  return category?.value?.iconUrl;
});
const canSave = computed(() => {
  return (
    rangeError.value === "" &&
    (category.value.swedish ||
      category.value.danish ||
      category.value.english ||
      category.value.norwegian) &&
    (category.value.iconUrl || base64.value)
  );
});
const uploadWidget = ref(null as PnFileUpload | null);
const saving = ref(false);
const isError = ref(false);
const errorDescription = ref("");

const resetError = () => {
  isError.value = false;
  errorDescription.value = "";
};

const filesAdded = (e: any) => {
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);

    if (errorFiles.length > 0) {
      errorFiles.forEach((d: any) => {
        e.target.removeFile(d);
      });
    } else {
      uploadError.value = "";
      e.target.startUpload();
    }
  }
};

const uploadFile = async (e: any) => {
  const file: PnUploadFileItem = e.detail.file;
  base64.value = file.base64;
  filename.value = file.filename;
  await uploadWidget.value?.removeFile(file);
};

const fileuploaderror = (e: any) => {
  uploadError.value = e.detail.message.toString();
};

const save = async () => {
  try {
    resetError();
    if (!category.value) {
      return;
    }
    saving.value = true;
    const uploadObj = {} as any;
    uploadObj.hidden = category?.value?.hidden ?? false;

    if (rangeStart.value && rangeEnd.value) {
      uploadObj.startDate = local2utc(rangeStart.value, "00:00:01");
      uploadObj.endDate = local2utc(rangeEnd.value);
    }

    uploadObj.swedish = category.value.swedish || "";
    uploadObj.danish = category.value.danish || "";
    uploadObj.english = category.value.english || "";
    uploadObj.norwegian = category.value.norwegian || "";

    if (base64.value) {
      uploadObj.base64 = base64.value;
      uploadObj.filename = filename.value;
    }

    await imageStore.saveImageCategory(categoryId, uploadObj);
    router.push("/categories");
  } catch (e) {
    isError.value = true;
    errorDescription.value = "Failed to save category. Please try again.";
    window.scrollTo(0, 0);
    console.log(e);
  } finally {
    saving.value = false;
  }
};
const currentRange = computed(() => {
  if (!category.value.startDate || !category.value.endDate) {
    return "Not set";
  }
  const from = utc2local(category.value.startDate).substring(0, 10);
  const to = utc2local(category.value.endDate).substring(0, 10);
  return `from ${from} to ${to} (repeats every year)`;
});

onMounted(async () => {
  if (!range.value) {
    throw new Error("invalid setup");
  }
  if (uploadWidget.value) {
    uploadWidget.value.addEventListener("filesAdded", filesAdded);
    uploadWidget.value.addEventListener("uploadFile", uploadFile);
    uploadWidget.value.addEventListener("fileuploaderror", fileuploaderror);
  }
  if (categoryId !== "new") {
    category.value = await imageStore.getImageCategory(categoryId);
  }
  if (category.value.startDate && category.value.endDate) {
    range.value.start = utc2local(category.value.startDate);
    range.value.end = utc2local(category.value.endDate);
    rangeChange.value = Math.random();
  }
});
const setRange = (e: { detail: { start: string; end: string } }) => {
  rangeChange.value = Math.random();

  if (!e.detail || !e.detail.start || !e.detail.end) {
    rangeError.value = "invalid range";
    return;
  }

  let start = new Date(e.detail.start);
  let end = new Date(e.detail.end);
  console.log(start);
  console.log(end);

  if (start.getTime() > end.getTime()) {
    rangeError.value = "Range was inverted";
  } else if (start.getTime() + 366 * 24 * 3600 * 1000 < end.getTime()) {
    rangeError.value = "Range must be less than one year";
  } else {
    rangeStart.value = e.detail.start;
    rangeEnd.value = e.detail.end;
    rangeError.value = "";
  }
};
const clearRange = () => {
  rangeChange.value = Math.random();
  rangeEnd.value = "";
  rangeStart.value = "";
};
</script>

<template>
  <pn-toast
    appearance="error"
    v-show="isError"
    :text="errorDescription"
  ></pn-toast>
  <h2>Edit category #{{ category.categoryId }}</h2>
  <h3>Labels</h3>
  <div>
    <p>
      if a language label is empty, the category will not show for that language
    </p>
    <label class="dist"> English </label>
    <pn-input
      :disabled="category.hidden"
      name="english"
      v-model="category.english"
    />
    <label class="dist"> Swedish </label>
    <pn-input
      :disabled="category.hidden"
      name="swedish"
      v-model="category.swedish"
    />
    <label class="dist"> Danish </label>
    <pn-input
      :disabled="category.hidden"
      name="danish"
      v-model="category.danish"
    />
    <label class="dist"> Norwegian </label>
    <pn-input
      :disabled="category.hidden"
      name="norwegian"
      v-model="category.norwegian"
    />
  </div>
  <p>
    <label for="hidden">
      <pn-checkbox
        checkboxid="hidden"
        :checked="category.hidden"
        @change="category.hidden = $event.target.checked"
      />
      Completely hide category
    </label>
  </p>

  <h3>Category icon</h3>
  <div class="grid-two-columns">
    <div class="icon">
      <img v-show="iconUrl" :src="iconUrl" />
      <div v-show="!iconUrl">
        <pn-icon :icon="disabled" class="middle" color="gray400"></pn-icon>
      </div>
    </div>

    <div>
      <pn-file-upload
        ref="uploadWidget"
        accept=".png"
        max-size="1mb"
        disabled="false"
        multiple="no"
        show-spinner="true"
      >
        <div class="message">
          <p class="title">
            <pn-icon :icon="image"></pn-icon>
            <pn-icon :icon="download"></pn-icon>
            <pn-icon :icon="inbox_in"></pn-icon>
          </p>
          <p class="description">
            360x300px, png, transparent background<br />
            To {{ category.iconUrl ? "Replace" : "Upload" }}, drag image here or
            <a href="#" class="pn-open-file-explorer">browse</a>
          </p>
        </div>
      </pn-file-upload>
      <pn-toast
        appearance="error"
        v-show="uploadError"
        :text="uploadError"
      ></pn-toast>
    </div>
  </div>
  <p></p>

  <h3>Only show between dates</h3>
  <p>Current value: {{ currentRange }}</p>
  <p>You can change these dates using this datepicker</p>
  <p>
    <pn-date-picker ref="range" range="true" @dateselection="setRange" />
    <pn-button
      v-show="rangeStart || rangeEnd"
      @click="clearRange"
      :key="rangeChange"
      >Clear range</pn-button
    >
    <pn-toast
      v-show="!rangeStart && !rangeEnd"
      :key="rangeChange"
      closable="false"
      >No range selected</pn-toast
    >
    <pn-toast appearance="warning" v-show="rangeError">{{
      rangeError
    }}</pn-toast>
  </p>
  <div>
    <hr />
    <span v-show="canSave" style="display: block; margin-top: 2rem">
      <pn-button @click="save">Save changes</pn-button>
    </span>
  </div>
</template>

<style lang="scss" scoped>
pn-file-upload {
  .pn-file-upload-container {
    width: 400px;
  }
  pn-icon svg {
    width: 6rem;
    height: 6rem;
  }
}

.target {
  border-top: 3px dashed gray;
}

td > pn-icon {
  padding-bottom: 0.3rem;
}

.icon {
  min-width: 200px;
  min-height: 200px;
  padding: 1rem;

  img {
    border: 1px dashed gray;
    border-radius: 1rem;
    width: 200px;
    margin: auto;
  }
}
</style>
